import React, { useState } from "react"
import { useKnowledgeSearch } from "../hooks/useKnowledgeSearch"
import { Combobox, Group, Loader, Text, TextInput, useCombobox } from "@mantine/core"
import { buildKnowledgeSearchResultsPageUrl, toKnowledgeArticleUrl } from "../utils/knowledgeBase"
import { MagnifyingGlassIcon } from "@phosphor-icons/react"

const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer')
}

export const KnowledgeSearch: React.FC = () => {
  const [term, setTerm] = useState('')
  const { articles, total, hasEnoughInput, isLoading, error } = useKnowledgeSearch(term)
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  const handleOptionSubmit = (value: string) => {
    const article = articles.find(a => String(a.id) === value)
    if (!article) return

    // 想定外のURLを新規タブで開かないよう、ナレッジベース配下に限定する
    const url = toKnowledgeArticleUrl(article.url)
    if (!url) return
    openInNewTab(url)
    combobox.closeDropdown()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return
    if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) return
    if (combobox.dropdownOpened && combobox.getSelectedOptionIndex() !== -1) return

    const query = term.trim()
    if (!query) return
    openInNewTab(buildKnowledgeSearchResultsPageUrl(query))
    combobox.closeDropdown()
  }

  const renderOptions = () => {
    if (error) {
      return (
        <Combobox.Empty>
          <Text fz={12} c="red">検索に失敗しました。時間をおいて再度お試しください</Text>
        </Combobox.Empty>
      )
    }
    if (articles.length > 0) {
      return articles.map(article => (
        <Combobox.Option value={String(article.id)} key={article.id}>
          <Text fz={13} lh={1.4} lineClamp={2}>{article.title}</Text>
          {article.category && <Text fz={11} c="dimmed">{article.category}</Text>}
        </Combobox.Option>
      ))
    }
    if (isLoading) return <Combobox.Empty>検索中...</Combobox.Empty>
    return <Combobox.Empty>該当する記事が見つかりません</Combobox.Empty>
  }

  const showRemainingHint = !error && articles.length > 0 && total > articles.length

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleOptionSubmit}
      position="bottom-end"
      width={320}
    >
      <Combobox.Target>
        <TextInput
          value={term}
          onChange={event => {
            setTerm(event.currentTarget.value)
            combobox.openDropdown()
            combobox.resetSelectedOption()
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          onKeyDown={handleKeyDown}
          placeholder="マニュアルを検索"
          aria-label="マニュアルを検索"
          leftSection={<MagnifyingGlassIcon size={14} />}
          rightSection={isLoading ? <Loader size={12} /> : null}
          size="xs"
          w={300}
        />
      </Combobox.Target>

      <Combobox.Dropdown hidden={!hasEnoughInput}>
        <Combobox.Options>{renderOptions()}</Combobox.Options>
        {showRemainingHint && (
          <Combobox.Footer>
            <Group justify="space-between" gap={8} wrap="nowrap">
              <Text fz={11} c="dimmed">Enterで検索</Text>
              <Text fz={11} c="dimmed">全 {total} 件</Text>
            </Group>
          </Combobox.Footer>
        )}
      </Combobox.Dropdown>
    </Combobox>
  )
}
